// /desktop/authorize: the normal-browser page where a signed-in user allows or cancels a
// Sidetone desktop sign-in (B4 seam; logic in ./authorize.ts). It sits outside the workspace
// shell: no phone, event stream or workspace data loads here. The URL is never rewritten, so the
// request survives sign-in, the verification-code step, workspace choice and reload.
import type { Session } from '@bcio-voice/contracts';
import { type ReactNode, useEffect, useState, useSyncExternalStore } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ApiClient } from '../lib/api.ts';
import type { ErrorView } from '../lib/errorCopy.ts';
import { Link } from '../lib/router.tsx';
import { APP_NAME, BRAND_ASSETS, windowTitle } from '../config/brand.ts';
import { BrandImage } from '../ui/Wordmark.tsx';
import { Icon } from '../ui/Icon.tsx';
import { StatePanel } from '../ui/StatePanel.tsx';
import { Diagnostics } from '../ui/Diagnostics.tsx';
import type { AuthState } from '../state/session.ts';
import { type AuthorizeSnapshot, DesktopAuthorizeController } from './authorize.ts';

export type AuthorizeScreen = 'desktop_app' | 'invalid_link' | 'loading' | 'boot_error' | 'sign_in' | 'choose_tenant' | 'confirm';

/**
 * Which screen the route shows. The packaged desktop renderer never shows a password form here:
 * its sign-in runs in the main process and finishes in the system browser.
 */
export function authorizeScreen(auth: AuthState['kind'], desktop: boolean, request: string | null): AuthorizeScreen {
  if (desktop) return 'desktop_app';
  if (!request) return 'invalid_link';
  switch (auth) {
    case 'booting':
      return 'loading';
    case 'boot_error':
      return 'boot_error';
    case 'signed_out':
    case 'expired_in_call':
      return 'sign_in';
    case 'choose_tenant':
      return 'choose_tenant';
    default:
      return 'confirm';
  }
}

export const AUTHORIZE_SIGN_IN_CONTEXT = {
  title: 'Sign in to connect Sidetone desktop',
  body: 'Sidetone desktop asked to sign in. Sign in here as usual, including your verification code, then confirm on the next step. Nothing is shared until you allow it.',
};
export const AUTHORIZE_TENANT_CONTEXT = 'Choose the workspace Sidetone desktop will use. You confirm on the next step.';

/** The card every screen of this route uses. */
export function AuthorizeFrame({ title, children, foot }: { title: string; children: ReactNode; foot?: ReactNode }) {
  useEffect(() => { document.title = windowTitle('Connect Sidetone desktop'); }, []);
  return (
    <main id="main" className="auth-page">
      <div className="auth-card authorize-card">
        <p className="auth-brand"><BrandImage asset={BRAND_ASSETS.lockup} height={32} /></p>
        <h1 className="auth-title">{title}</h1>
        {children}
        {foot === undefined ? <p className="auth-foot">Only continue if you just chose Continue in your browser in Sidetone desktop.</p> : foot}
      </div>
    </main>
  );
}

/** Packaged desktop renderer: this route is the browser's half of desktop sign-in, not the app's. */
export function DesktopAppNotice() {
  return (
    <AuthorizeFrame title="Finish in your browser" foot={<p className="auth-foot">Your password and verification code are entered in your browser, never in this app.</p>}>
      <p className="field-hint">{APP_NAME} desktop is signed in through your web browser. In {APP_NAME}, choose Continue in your browser, then allow the sign-in on the page that opens there.</p>
      <div className="form-actions"><Link to="/" className="btn btn-primary">Back to {APP_NAME}</Link></div>
    </AuthorizeFrame>
  );
}

/** A missing, malformed or altered request link: nothing is sent to the server. */
export function InvalidAuthorizeLink() {
  return (
    <AuthorizeFrame title="This sign-in link isn’t valid" foot={null}>
      <p className="field-hint">Only the link Sidetone desktop opens works here. In Sidetone desktop, choose Continue in your browser to start again.</p>
      <div className="form-actions"><Link to="/" className="btn">Go to {APP_NAME}</Link></div>
    </AuthorizeFrame>
  );
}

export interface DesktopAuthorizePageProps {
  api: Pick<ApiClient, 'request'>;
  request: string;
  session: Session;
  /** The normal workspace switch (POST /auth/tenant); the page reloads details for the new workspace. */
  onSwitchTenant: (tenantId: string) => Promise<ErrorView | null>;
  onSignOut: () => void;
  /** The server wants a fresh sign-in (session or verification code): end this one and sign in again. */
  onReauthenticate: () => void;
  /** Re-reads /auth/session (another tab may have changed the workspace). */
  onReloadSession: () => Promise<void>;
  /** Tests inject a prepared controller; the browser creates one that opens via location.assign. */
  controller?: DesktopAuthorizeController;
}

export function DesktopAuthorizePage({ api, request, session, onSwitchTenant, onSignOut, onReauthenticate, onReloadSession, controller: injected }: DesktopAuthorizePageProps) {
  const [controller] = useState(() => injected ?? new DesktopAuthorizeController({ api, request, open: url => window.location.assign(url) }));
  const snap = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const userId = session.user?.id ?? null;
  const tenantId = session.tenant?.id ?? null;
  useEffect(() => { controller.setSession(userId, tenantId); }, [controller, userId, tenantId]);
  useEffect(() => () => controller.dispose(), [controller]);

  return (
    <AuthorizeFrame title={titleFor(snap)}>
      <AuthorizeBody snap={snap} session={session} controller={controller} onSwitchTenant={onSwitchTenant} onSignOut={onSignOut} onReauthenticate={onReauthenticate} onReloadSession={onReloadSession} />
    </AuthorizeFrame>
  );
}

export function titleFor(snap: AuthorizeSnapshot): string {
  switch (snap.phase) {
    case 'loading':
    case 'ready':
    case 'deciding':
      return 'Allow Sidetone desktop to sign in?';
    case 'opening':
      return 'Opening Sidetone desktop';
    case 'denied':
    case 'cancelled':
      return 'Sign-in cancelled';
    case 'reauth':
      return 'Sign in again to continue';
    case 'tenant_mismatch':
    case 'superseded':
      return 'Your workspace changed';
    case 'expired':
      return 'This sign-in request has ended';
    case 'unavailable':
      return 'Desktop sign-in isn’t available yet';
    case 'unknown':
      return 'We couldn’t confirm your choice';
    case 'invalid_response':
      return 'Nothing was opened';
    default:
      return snap.decision ? 'Your choice wasn’t saved' : 'Couldn’t load this request';
  }
}

function expiryTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

type BodyProps = Omit<DesktopAuthorizePageProps, 'api' | 'request' | 'controller'> & { snap: AuthorizeSnapshot; controller: DesktopAuthorizeController };

function AuthorizeBody({ snap, session, controller, onSwitchTenant, onSignOut, onReauthenticate, onReloadSession }: BodyProps) {
  const diagnostics = snap.diagnostic ? <Diagnostics diagnostic={{ code: snap.diagnostic.code, detail: null, requestId: snap.diagnostic.requestId }} /> : null;
  const action = (label: string, onClick: () => void, primary = false) => (
    <div className="form-actions"><button type="button" className={primary ? 'btn btn-primary' : 'btn'} onClick={onClick}>{label}</button></div>
  );

  switch (snap.phase) {
    case 'loading':
    case 'ready':
    case 'deciding':
      return <Confirm snap={snap} session={session} controller={controller} onSwitchTenant={onSwitchTenant} onSignOut={onSignOut} />;
    case 'opening':
      return (
        <div className="auth-form" role="status">
          <p>If your browser asks, choose to open {APP_NAME}. You can close this page once {APP_NAME} desktop shows you’re signed in.</p>
          {snap.canOpen ? (
            <div className="form-actions"><button type="button" className="btn btn-primary" onClick={() => controller.openAgain()}><Icon icon={ExternalLink} /> Open {APP_NAME}</button></div>
          ) : (
            <p className="field-hint">If {APP_NAME} didn’t open, start again from the desktop app: choose Continue in your browser.</p>
          )}
        </div>
      );
    case 'denied':
      return (
        <div className="auth-form" role="status">
          <p>{APP_NAME} desktop wasn’t signed in. If your browser asks, choose to open {APP_NAME} so the desktop app knows.</p>
          {snap.canOpen ? (
            <div className="form-actions"><button type="button" className="btn" onClick={() => controller.openAgain()}><Icon icon={ExternalLink} /> Open {APP_NAME}</button></div>
          ) : <p className="field-hint">You can close this page.</p>}
        </div>
      );
    case 'cancelled':
      return <p className="field-hint" role="status">Nothing was passed to {APP_NAME} desktop from this page. If the desktop app is still waiting, choose Cancel there. You can close this page.</p>;
    case 'reauth':
      return (
        <div className="auth-form">
          <p className="field-hint">To connect a desktop app, confirm it’s you: sign in again with your password and verification code. Your request stays on this page.</p>
          {diagnostics}
          {action('Sign in again', onReauthenticate, true)}
        </div>
      );
    case 'tenant_mismatch':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">The workspace you’re signed in to changed, perhaps in another tab. Check again to see which workspace Sidetone desktop would use before you choose.</p>
          {diagnostics}
          {action('Check again', () => { void onReloadSession().then(() => controller.recheck()); }, true)}
        </div>
      );
    case 'superseded':
      return <p className="field-hint" role="alert">Your account or workspace changed while this was being confirmed, so nothing was passed to {APP_NAME} desktop. In the desktop app, choose Continue in your browser to start again.</p>;
    case 'expired':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">It expired or was already used. In {APP_NAME} desktop, choose Continue in your browser to start again.</p>
          {diagnostics}
        </div>
      );
    case 'unavailable':
      return (
        <>
          <StatePanel variant="unconfigured" compact title="This server isn’t set up for desktop sign-in" body={`Use ${APP_NAME} in your web browser for now.`} action={<button type="button" className="btn" onClick={() => controller.recheck()}>Try again</button>} />
          {diagnostics}
        </>
      );
    case 'unknown':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">The connection dropped before the server answered, so your choice may or may not have been saved. It won’t be sent again automatically. Check again to see whether this request is still waiting.</p>
          {diagnostics}
          {action('Check again', () => controller.recheck(), true)}
        </div>
      );
    case 'invalid_response':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">The server’s answer didn’t match what {APP_NAME} desktop expects, so nothing was opened. In the desktop app, choose Continue in your browser to start again.</p>
          {diagnostics}
        </div>
      );
    default:
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">{snap.decision ? `Nothing was passed to ${APP_NAME} desktop. Try again to reload the request, then choose again.` : 'Check your connection, then try again.'}</p>
          {diagnostics}
          {action('Try again', () => controller.recheck(), true)}
        </div>
      );
  }
}

function Confirm({ snap, session, controller, onSwitchTenant, onSignOut }: Omit<BodyProps, 'onReauthenticate' | 'onReloadSession'>) {
  const [choosing, setChoosing] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const user = session.user;
  const tenant = session.tenant;
  const ready = snap.phase === 'ready' && snap.view !== null;
  const deciding = snap.phase === 'deciding';
  const others = session.memberships.filter(m => m.tenant_id !== tenant?.id);
  const locked = deciding || switching !== null;

  const switchTo = async (id: string) => {
    if (locked) return;
    setSwitching(id);
    setSwitchError(null);
    const error = await onSwitchTenant(id);
    setSwitching(null);
    if (error) setSwitchError(error.title);
    else setChoosing(false);
  };

  return (
    <div className="auth-form" aria-busy={snap.phase === 'loading' || deciding || undefined}>
      <dl className="authorize-who">
        <div>
          <dt>Signed in as</dt>
          <dd><strong>{user?.display_name}</strong> <span className="meta">{user?.email}</span></dd>
        </div>
        <div>
          <dt>Workspace</dt>
          <dd><strong>{tenant?.name}</strong></dd>
        </div>
      </dl>
      <div className="authorize-switch">
        {others.length > 0 ? (
          <button type="button" className="btn btn-quiet" aria-expanded={choosing} aria-controls="authorize-workspaces" onClick={() => setChoosing(c => !c)} disabled={locked}>Use a different workspace</button>
        ) : null}
        <button type="button" className="btn btn-quiet" onClick={onSignOut} disabled={locked}>Not you? Sign out</button>
      </div>
      {choosing && others.length > 0 ? (
        <ul id="authorize-workspaces" className="choice-list" aria-label="Your other workspaces">
          {others.map(m => (
            <li key={m.tenant_id}>
              <button type="button" className="choice" onClick={() => void switchTo(m.tenant_id)} disabled={locked} aria-busy={switching === m.tenant_id || undefined}>
                <span className="choice-title">{m.name}</span>
                <span className="choice-meta">Switch, then confirm for this workspace</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {switchError ? <p className="form-error" role="alert">{switchError}</p> : null}

      <div className="authorize-scope">
        <p>If you allow it, {APP_NAME} desktop on this computer can:</p>
        <ul>
          <li>Use {tenant?.name ?? 'this workspace'} as you, with the same access you have here. Your role and permissions don’t change.</li>
          <li>Stay signed in on that computer until you sign out there, the sign-in expires, or your access changes.</li>
        </ul>
        <p className="meta">It doesn’t get more than your current access. Recording and transcription still follow each call’s own agreement and controls.</p>
      </div>

      {snap.phase === 'loading' ? <p className="field-hint" role="status">Checking this request…</p> : null}
      {snap.view ? <p className="meta">This request expires at {expiryTime(snap.view.expires_at)}.</p> : null}
      {snap.retryNotice ? <p className="notice notice-warn" role="status">Your session was refreshed before your choice was used. Nothing was passed to {APP_NAME} desktop. Choose again.</p> : null}

      <div className="form-actions">
        <button
          type="button"
          className="btn"
          disabled={!(ready || (deciding && snap.decision === 'allow'))}
          aria-busy={deciding && snap.decision === 'deny' ? true : undefined}
          onClick={() => { if (deciding) controller.abandon(); else void controller.decide('deny'); }}
        >
          {deciding && snap.decision === 'deny' ? 'Cancelling…' : 'Cancel'}
        </button>
        <button type="button" className="btn btn-primary" disabled={!ready || choosing} aria-busy={deciding && snap.decision === 'allow' ? true : undefined} onClick={() => void controller.decide('allow')}>
          {deciding && snap.decision === 'allow' ? 'Allowing…' : 'Allow'}
        </button>
      </div>
    </div>
  );
}
